import { CoordinatorService } from './coordinator.service';
import { createToken, Lexer, CstParser, CstNode } from 'chevrotain';

const IgnoreChars = createToken({
    name: 'Ignore',
    pattern: /[\s]+/,
});

const And = createToken({
    name: 'And',
    pattern: /\s+AND\s+/i,
});

const Or = createToken({
    name: 'Or',
    pattern: /\s+OR\s+/i,
});

const Not = createToken({
    name: 'Not',
    pattern: /\s+NOT\s+/i,
});

const QuotedString = createToken({
    name: 'QuotedString',
    pattern: /"([^"\\]|\\.)*"/,
});

const Literal = createToken({
    name: 'Literal',
    pattern: /\S+/,
});

const RegexpSource = createToken({
    name: 'RegexpSource',
    pattern: /\/([^\/\\]|\\.)*\//,
});

const tokens = [
    // WhiteSpace,
    QuotedString,
    RegexpSource,
    And,
    Or, 
    Not,
    IgnoreChars,
    Literal,
];

const lexer = new Lexer(tokens);
// Define the parser class
class TextParser extends CstParser {
    expression: any;
    visitor: any;

    constructor() {
        super(tokens);
        const $ = this;

        const term = $.RULE('term', () => {
            $.OR([
                { ALT: () => this.CONSUME(Literal) },
                { ALT: () => this.CONSUME(QuotedString) },
                { ALT: () => this.CONSUME(RegexpSource) },
            ]);
        });
        const whitespace = $.RULE('whitespace', () => this.CONSUME(IgnoreChars));

        const operators = $.RULE('operators', () => {
            $.OR([
                { ALT: () => this.CONSUME(And) },
                { ALT: () => this.CONSUME(Or) },
                { ALT: () => this.CONSUME(Not) },
                { ALT: whitespace },
            ]);
        });

        this.expression = $.RULE('expression', () => {
            $.SUBRULE(term);
            $.MANY(() => {
                $.SUBRULE2(operators);
                $.SUBRULE2(term);
            });
        });

        this.performSelfAnalysis();
    }
}

const parser = new TextParser();
const baseVisitor = parser.getBaseCstVisitorConstructor();
class ExtractorVisitor extends baseVisitor {
    private _tokenLs: { value: string; start: number; isOperator?: boolean }[] = [];
    private _highlightSet = new Set<string>();

    constructor() {
        super();
        this.validateVisitor();
    }

    term(ctx: any) {
        let x = (ctx.Literal || ctx.QuotedString || ctx.RegexpSource || ctx.Fun)[0];

        if (ctx.fun) {
            console.log(`FUN: `, x.image);
        }

        let value = x.image;
        if (value.startsWith('"')) value = value.slice(1, -1);
        const start = x.startOffset;

        this._tokenLs.push({
            value,
            start,
        });
    }

    operators(ctx: any) {
        // console.log(`OPERATOR XX: `, ctx);
        let x = ctx.And || ctx.Or || ctx.Not || ctx.Near || ctx.Follow;
        if (!x) return;
        x = x[0];

        const value = `.${x.image.trim().toLowerCase()}`;
        const start = x.startOffset;
        const isOperator = true;

        this._tokenLs.push({
            value,
            start,
            isOperator,
        });
    }

    whitespace(ctx: any) {
        console.log(`WHITESPACE: `, ctx);
    }

    expression(ctx: any) {
        ctx.operators &&
            ctx.operators.forEach((op: any) => {
                this.visit(op);
            });

        ctx.term &&
            ctx.term.forEach((term: any) => {
                this.visit(term);
            });
    }

    private _finalizeTerm(field: string, ls: string[], argLs: string[] = []) {
        let v = ls.join(' ');
        this._highlightSet.add(v);
        if (!v.startsWith('/')) v = `\`${v}\``;

        return `("${field}", ${v}${argLs.slice(1).join(',')})`;
    }

    parse(field: string, cstNode: CstNode) {
        this._tokenLs = [];
        this._highlightSet.clear();
        this.visit(cstNode);
        this._tokenLs.sort((a, b) => a.start - b.start);

        const outputLs: string[] = ['.and'];
        const termLs: string[] = [];
        this._tokenLs.forEach((x, i) => {
            if (x.isOperator) {
                outputLs.push(this._finalizeTerm(field, termLs, x.value.split('-')));
                outputLs.push(x.value);
                termLs.length = 0;
            } else {
                termLs.push(x.value);
            }
        });
        outputLs.push(this._finalizeTerm(field, termLs));

        return { interpretation: outputLs.join(''), highlightLs: Array.from(this._highlightSet) };
    }
}

const extractorVisitor = new ExtractorVisitor();
export class SearchStringParser {
    constructor(
        private coords: CoordinatorService,
        public field: string,
        private _isPreview = false,
    ) {}

    parse(txt: string) {
        // console.log(`Parsed string: ||${txt}||`);

        txt = txt.trim();
        const isStartNot = txt.substring(0, 4).toLowerCase() === 'not ';
        if (isStartNot) txt = txt.substring(4).trim();

        const lexingResult = lexer.tokenize(txt.trim());
        parser.input = lexingResult.tokens;

        if (parser.errors.length > 0) {
            for (const error of parser.errors) {
                console.log(error.message);
            }
            throw new Error('Parsing errors detected!');
        }

        const extracted = extractorVisitor.parse(this.field, parser.expression());

        let suffix = extracted.interpretation;
        if (isStartNot) suffix = `.not${suffix.substring(4)}`;

        const resp = {
            interpretation: `return ${this._isPreview ? '$preview' : '$collection'}${suffix}`,
            highlightLs: extracted.highlightLs,
        };

        // console.log('STRING PARSE RESPONSE: ', resp);
        return resp;
    }
}
